import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ELLaiLogo from '../components/ELLaiLogo';
import { api } from '../services/api';
import './LoginPage.css';

// Shared with SignupPage — simple strength heuristic
export function passwordStrength(password) {
  if (!password) return { score: 0, label: '', color: '#ddd' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score <= 1) return { score, label: 'Weak', color: '#d9534f' };
  if (score <= 3) return { score, label: 'Fair', color: '#f0ad4e' };
  return { score, label: 'Strong', color: '#5cb85c' };
}

export function PasswordMeter({ password }) {
  const { score, label, color } = passwordStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: '6px' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{
            flex: 1, height: '4px', borderRadius: '2px',
            background: i <= score ? color : '#e5e0d8',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>
      <span style={{ fontSize: '12px', color }}>{label}</span>
    </div>
  );
}

function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);

    try {
      const response = await api.post('/api/auth/reset-password', { token, password });
      const data = await response.json();

      if (response.ok) {
        setDone(true);
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(data.error || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-content">
          <div className="brand">
            <ELLaiLogo size="medium" />
          </div>

          <div className="card login-card">
            {done ? (
              <>
                <h2>Password updated ✓</h2>
                <p className="subtitle mb-lg">
                  Your password has been reset. Taking you to sign in...
                </p>
                <Link to="/login" className="btn btn-primary btn-full">
                  Sign In Now
                </Link>
              </>
            ) : (
              <>
                <h2>Choose a new password</h2>
                <p className="subtitle mb-lg">
                  Must be at least 8 characters with a letter and a number.
                </p>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="password">New Password</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoFocus
                      minLength={8}
                    />
                    <PasswordMeter password={password} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirm">Confirm Password</label>
                    <input
                      id="confirm"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={8}
                    />
                    {confirm && confirm !== password && (
                      <span style={{ fontSize: '12px', color: '#d9534f' }}>
                        Passwords don't match
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-full"
                    disabled={loading || !password || password !== confirm}
                  >
                    {loading ? 'Updating...' : 'Reset Password'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
